import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import './ReaderProfile.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const PACE_OPTIONS = [
  { value: 0, label: 'Light', desc: '1-2 books/month' },
  { value: 1, label: 'Moderate', desc: '3-5 books/month' },
  { value: 2, label: 'Avid', desc: '6-10 books/month' },
  { value: 3, label: 'Voracious', desc: '10+ books/month' },
];

const FACT_FICTION_OPTIONS = [
  { value: 0, label: 'Fiction', emoji: '📖' },
  { value: 1, label: 'Fact', emoji: '📚' },
  { value: 2, label: 'Both', emoji: '📖📚' },
];

const DIFFICULTY_LABELS = ['Light', 'Accessible', 'Moderate', 'Challenging', 'Demanding'];

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'no', label: 'Norwegian', flag: '🇳🇴' },
  { code: 'sv', label: 'Swedish', flag: '🇸🇪' },
  { code: 'da', label: 'Danish', flag: '🇩🇰' },
  { code: 'de', label: 'German', flag: '🇩🇪' },
  { code: 'fr', label: 'French', flag: '🇫🇷' },
  { code: 'es', label: 'Spanish', flag: '🇪🇸' },
  { code: 'it', label: 'Italian', flag: '🇮🇹' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { code: 'nl', label: 'Dutch', flag: '🇳🇱' },
  { code: 'fi', label: 'Finnish', flag: '🇫🇮' },
  { code: 'pl', label: 'Polish', flag: '🇵🇱' },
  { code: 'ja', label: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', label: 'Korean', flag: '🇰🇷' },
  { code: 'zh', label: 'Chinese', flag: '🇨🇳' },
  { code: 'ru', label: 'Russian', flag: '🇷🇺' },
];

function ReaderProfile({ factFictionMode }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [genreGroups, setGenreGroups] = useState([]);
  const [blockedBooks, setBlockedBooks] = useState([]);
  const [showBlocked, setShowBlocked] = useState(false);

  const [form, setForm] = useState({
    factFictionPreference: 0,
    readingPace: 1,
    preferredDifficulty: 3,
    bio: '',
    favoriteAuthors: '',
    preferredLanguages: '',
    genreIds: [],
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(instance, '/api/readerprofile');
      if (res.status === 404) {
        setIsNew(true);
        setEditing(true);
        return;
      }
      if (!res.ok) throw new Error('Failed to load profile.');
      const data = await res.json();
      setProfile(data);
      setIsNew(false);
    } catch (err) {
      setError(err.message || 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  const fetchGenres = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/readerprofile/genres');
      setGenreGroups(unwrap(data));
    } catch {
      // Genres are non-critical; form still works without them
    }
  }, [instance]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (editing) fetchGenres();
  }, [editing, fetchGenres]);

  const startEdit = useCallback(() => {
    if (profile) {
      const preferredGenres = unwrap(profile.preferredGenres);
      setForm({
        factFictionPreference: profile.factFictionPreference ?? 0,
        readingPace: profile.readingPace ?? 1,
        preferredDifficulty: profile.preferredDifficulty ?? 3,
        bio: profile.bio || '',
        favoriteAuthors: profile.favoriteAuthors || '',
        preferredLanguages: profile.preferredLanguages || '',
        genreIds: preferredGenres.map(g => g.id),
      });
    }
    setEditing(true);
  }, [profile]);

  const cancelEdit = useCallback(() => {
    if (isNew) return;
    setEditing(false);
  }, [isNew]);

  const toggleGenre = useCallback((id) => {
    setForm(prev => ({
      ...prev,
      genreIds: prev.genreIds.includes(id)
        ? prev.genreIds.filter(g => g !== id)
        : [...prev.genreIds, id],
    }));
  }, []);

  const toggleLanguage = useCallback((code) => {
    setForm(prev => {
      const current = prev.preferredLanguages ? prev.preferredLanguages.split(',').filter(Boolean) : [];
      const updated = current.includes(code)
        ? current.filter(c => c !== code)
        : [...current, code];
      return { ...prev, preferredLanguages: updated.join(',') };
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const method = isNew ? 'POST' : 'PUT';
      const data = await apiFetchJson(instance, '/api/readerprofile', {
        method,
        body: JSON.stringify(form),
      });
      setProfile(data);
      setIsNew(false);
      setEditing(false);
    } catch {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  }, [instance, isNew, form]);

  const fetchBlockedBooks = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/userbookstatus?status=NotInterested');
      setBlockedBooks(unwrap(data));
    } catch { /* ignore */ }
  }, [instance]);

  const unblockBook = useCallback(async (bookId) => {
    try {
      await apiFetchJson(instance, `/api/userbookstatus/${bookId}`, { method: 'DELETE' });
      setBlockedBooks(prev => prev.filter(b => b.bookId !== bookId));
    } catch { /* ignore */ }
  }, [instance]);

  useEffect(() => {
    if (showBlocked) fetchBlockedBooks();
  }, [showBlocked, fetchBlockedBooks]);

  if (loading) {
    return (
      <div className="reader-profile-loading">
        <span className="reader-profile-spinner" />
        <p>{t('bookClub.readerProfile.loading')}</p>
      </div>
    );
  }

  if (error && !editing) {
    return (
      <div className="reader-profile-error">
        <span className="reader-profile-error-icon">⚠️</span>
        <p>{error}</p>
        <button className="reader-profile-btn" onClick={fetchProfile}>{t('bookClub.common.retry')}</button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="reader-profile-form">
        <h2 className="reader-profile-form-title">
          {isNew ? '✨ ' + t('bookClub.readerProfile.setup') : '✏️ ' + t('bookClub.readerProfile.editProfile')}
        </h2>

        {error && <div className="reader-profile-form-error">{error}</div>}

        {/* Fact / Fiction Preference */}
        <fieldset className="reader-profile-fieldset">
          <legend>{t('bookClub.readerProfile.whatDoYouRead')}</legend>
          <div className="reader-profile-option-group">
            {FACT_FICTION_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`reader-profile-option-btn ${form.factFictionPreference === opt.value ? 'active' : ''}`}
                onClick={() => setForm(f => ({ ...f, factFictionPreference: opt.value }))}
              >
                <span className="reader-profile-option-emoji">{opt.emoji}</span>
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Reading Pace */}
        <fieldset className="reader-profile-fieldset">
          <legend>{t('bookClub.readerProfile.readingPace')}</legend>
          <div className="reader-profile-option-group">
            {PACE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`reader-profile-option-btn ${form.readingPace === opt.value ? 'active' : ''}`}
                onClick={() => setForm(f => ({ ...f, readingPace: opt.value }))}
              >
                <strong>{opt.label}</strong>
                <span className="reader-profile-option-desc">{opt.desc}</span>
              </button>
            ))}
          </div>
        </fieldset>

        {/* Difficulty */}
        <fieldset className="reader-profile-fieldset">
          <legend>{t('bookClub.readerProfile.preferredDifficulty')}</legend>
          <div className="reader-profile-slider-wrap">
            <input
              type="range"
              min="1"
              max="5"
              value={form.preferredDifficulty}
              onChange={e => setForm(f => ({ ...f, preferredDifficulty: Number(e.target.value) }))}
              className="reader-profile-slider"
            />
            <div className="reader-profile-slider-labels">
              {DIFFICULTY_LABELS.map((label, i) => (
                <span
                  key={i}
                  className={form.preferredDifficulty === i + 1 ? 'active' : ''}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </fieldset>

        {/* Language Preferences */}
        <fieldset className="reader-profile-fieldset">
          <legend>{t('bookClub.readerProfile.bookLanguages')}</legend>
          <p className="reader-profile-hint">
            {t('bookClub.readerProfile.languagesPlaceholder')}
          </p>
          <div className="reader-profile-lang-grid">
            {LANGUAGE_OPTIONS.map(lang => {
              const selected = form.preferredLanguages?.split(',').includes(lang.code);
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`reader-profile-lang-btn ${selected ? 'active' : ''}`}
                  onClick={() => toggleLanguage(lang.code)}
                >
                  <span className="reader-profile-lang-flag">{lang.flag}</span>
                  {lang.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Genres */}
        <fieldset className="reader-profile-fieldset">
          <legend>{t('bookClub.readerProfile.preferredGenres')}</legend>
          {genreGroups.length === 0 && <p className="reader-profile-muted">{t('bookClub.readerProfile.loadingGenres')}</p>}
          {genreGroups.map(group => (
            <div key={group.bookType} className="reader-profile-genre-group">
              <h4 className="reader-profile-genre-group-title">{group.bookType}</h4>
              {unwrap(group.genres).map(genre => (
                <div key={genre.id} className="reader-profile-genre-item">
                  <label className="reader-profile-checkbox">
                    <input
                      type="checkbox"
                      checked={form.genreIds.includes(genre.id)}
                      onChange={() => toggleGenre(genre.id)}
                    />
                    {genre.name}
                  </label>
                  {unwrap(genre.subGenres).length > 0 && (
                    <div className="reader-profile-subgenres">
                      {unwrap(genre.subGenres).map(sub => (
                        <label key={sub.id} className="reader-profile-checkbox">
                          <input
                            type="checkbox"
                            checked={form.genreIds.includes(sub.id)}
                            onChange={() => toggleGenre(sub.id)}
                          />
                          {sub.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </fieldset>

        {/* Bio */}
        <fieldset className="reader-profile-fieldset">
          <legend>{t('bookClub.readerProfile.aboutYou')}</legend>
          <textarea
            className="reader-profile-textarea"
            placeholder={t('bookClub.readerProfile.aboutPlaceholder')}
            value={form.bio}
            onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            rows={3}
          />
        </fieldset>

        {/* Favorite Authors */}
        <fieldset className="reader-profile-fieldset">
          <legend>{t('bookClub.readerProfile.favoriteAuthors')}</legend>
          <input
            type="text"
            className="reader-profile-input"
            placeholder={t('bookClub.readerProfile.favoriteAuthors')}
            value={form.favoriteAuthors}
            onChange={e => setForm(f => ({ ...f, favoriteAuthors: e.target.value }))}
          />
        </fieldset>

        <div className="reader-profile-form-actions">
          {!isNew && (
            <button type="button" className="reader-profile-btn secondary" onClick={cancelEdit}>
              {t('bookClub.common.cancel')}
            </button>
          )}
          <button
            type="button"
            className="reader-profile-btn primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('bookClub.readerProfile.saving') : isNew ? t('bookClub.readerProfile.createProfile') : t('bookClub.readerProfile.saveChanges')}
          </button>
        </div>
      </div>
    );
  }

  // Profile view
  const paceLabel = PACE_OPTIONS.find(p => p.value === profile.readingPace)?.label || 'Unknown';
  const paceDesc = PACE_OPTIONS.find(p => p.value === profile.readingPace)?.desc || '';
  const ffLabel = FACT_FICTION_OPTIONS.find(f => f.value === profile.factFictionPreference);
  const preferredGenres = unwrap(profile.preferredGenres);
  const selectedLangs = (profile.preferredLanguages || '').split(',').filter(Boolean);

  return (
    <div className="reader-profile-view">
      <div className="reader-profile-header">
        <h2>👤 {t('bookClub.readerProfile.title')}</h2>
        <button className="reader-profile-btn secondary" onClick={startEdit}>✏️ {t('bookClub.readerProfile.edit')}</button>
      </div>

      <div className="reader-profile-cards">
        {/* Pace */}
        <div className="reader-profile-card">
          <span className="reader-profile-card-label">{t('bookClub.readerProfile.readingPace')}</span>
          <span className="reader-profile-badge">{paceLabel}</span>
          <span className="reader-profile-card-sub">{paceDesc}</span>
        </div>

        {/* Difficulty */}
        <div className="reader-profile-card">
          <span className="reader-profile-card-label">{t('bookClub.readerProfile.difficulty')}</span>
          <span className="reader-profile-stars">
            {[1, 2, 3, 4, 5].map(n => (
              <span key={n} className={n <= profile.preferredDifficulty ? 'filled' : ''}>★</span>
            ))}
          </span>
          <span className="reader-profile-card-sub">
            {DIFFICULTY_LABELS[profile.preferredDifficulty - 1] || ''}
          </span>
        </div>

        {/* Fact / Fiction */}
        <div className="reader-profile-card">
          <span className="reader-profile-card-label">{t('bookClub.readerProfile.preference')}</span>
          <span className="reader-profile-pill">
            {ffLabel?.emoji} {ffLabel?.label}
          </span>
        </div>

        {/* Languages */}
        {selectedLangs.length > 0 && (
          <div className="reader-profile-card">
            <span className="reader-profile-card-label">{t('bookClub.readerProfile.languages')}</span>
            <span className="reader-profile-pill">
              {selectedLangs.map(code => {
                const l = LANGUAGE_OPTIONS.find(lo => lo.code === code);
                return l ? `${l.flag} ${l.label}` : code;
              }).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Bio */}
      {profile.bio && (
        <div className="reader-profile-section">
          <h3>{t('bookClub.readerProfile.about')}</h3>
          <p className="reader-profile-bio">{profile.bio}</p>
        </div>
      )}

      {/* Favorite Authors */}
      {profile.favoriteAuthors && (
        <div className="reader-profile-section">
          <h3>{t('bookClub.readerProfile.favoriteAuthors')}</h3>
          <p className="reader-profile-authors">{profile.favoriteAuthors}</p>
        </div>
      )}

      {/* Genres */}
      {preferredGenres.length > 0 && (
        <div className="reader-profile-section">
          <h3>{t('bookClub.readerProfile.genres')}</h3>
          <div className="reader-profile-tags">
            {preferredGenres.map(g => (
              <span key={g.id} className="reader-profile-tag">
                {g.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Blocked Books */}
      <div className="reader-profile-section">
        <div className="reader-profile-blocked-header">
          <h3>🚫 {t('bookClub.readerProfile.hiddenBooks')}</h3>
          <button
            className="reader-profile-btn secondary"
            onClick={() => setShowBlocked(prev => !prev)}
          >
            {showBlocked ? t('bookClub.readerProfile.hide') : `${t('bookClub.readerProfile.show')}${blockedBooks.length > 0 ? ` (${blockedBooks.length})` : ''}`}
          </button>
        </div>
        <p className="reader-profile-blocked-desc">
          {t('bookClub.readerProfile.hiddenDesc')}
        </p>
        {showBlocked && (
          <div className="reader-profile-blocked-list">
            {blockedBooks.length === 0 && (
              <p className="reader-profile-muted">{t('bookClub.readerProfile.noHiddenBooks')}</p>
            )}
            {blockedBooks.map(entry => (
              <div key={entry.bookId || entry.id} className="reader-profile-blocked-item">
                <div className="reader-profile-blocked-info">
                  <span className="reader-profile-blocked-title">{entry.book?.title || 'Unknown'}</span>
                  <span className="reader-profile-blocked-author">{entry.book?.author || ''}</span>
                </div>
                <button
                  className="reader-profile-btn reader-profile-btn-unblock"
                  onClick={() => unblockBook(entry.bookId)}
                >
                  ↩ {t('bookClub.readerProfile.unblock')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReaderProfile;
